import { clamp, wrapIndex } from '../core/math';
import { speedOf, type KartState } from '../physics/vehicle';
import type { Track } from '../track/generate';
import type { TrackQuery } from '../track/lookup';
import { maxBrakeDecel, type SpeedProfile } from '../track/speedprofile';
import type { Weather } from '../track/weather';

export interface BrakeScanOptions {
  /** Metres of track to look ahead. */
  scan: number;
  /** Fraction of the profile to aim for. Below 1 brakes earlier. */
  pace: number;
  /** Deceleration the tyres can actually deliver here, m/s². */
  brakeCap: number;
  /** Demand below this fraction of `brakeCap` asks for no pedal at all. */
  floor: number;
  /** Fraction of `brakeCap` spanned between no pedal and full pedal. */
  span: number;
}

/**
 * How hard to brake, from a forward scan of the speed profile.
 *
 * Ask, for every point in the next `scan` metres, whether the deceleration
 * needed to make that point's speed limit has grown past what the tyres can
 * deliver. A fixed lookahead distance cannot answer this: the distance needed
 * scales with the square of speed, so any single distance is far too short
 * somewhere and wastefully long everywhere else.
 *
 * `floor`/`span` shape the pedal. Committing below full capacity gets the
 * braking done before turn-in rather than dragging it into the corner, where it
 * would ask the rears to brake and corner at once.
 *
 * Shared by the reference {@link Autopilot} and by the player's
 * {@link AutoBrake}; the arithmetic is identical for both, so the headless
 * driver's traces stay byte-identical to what it produced before the split.
 */
export function scanBrakeDemand(
  track: Track,
  profile: SpeedProfile,
  fromIndex: number,
  speed: number,
  o: BrakeScanOptions,
): number {
  const n = track.center.length;
  const ds = track.spacing;
  const scanSteps = Math.round(o.scan / ds);
  let brake = 0;
  for (let s = 1; s <= scanSteps; s++) {
    const idx = wrapIndex(fromIndex + s, n);
    const limit = profile.vIdeal[idx] * o.pace;
    if (speed <= limit) continue;
    const distance = s * ds;
    const needed = (speed * speed - limit * limit) / (2 * distance);
    brake = Math.max(brake, clamp((needed / o.brakeCap - o.floor) / o.span, 0, 1));
  }
  return brake;
}

/**
 * Fraction of the scored speed profile automatic braking aims for.
 *
 * That profile is solved for a proper racing line (see `effectiveCurvature`),
 * and a player who is not steering one arrives at the apex on a tighter radius
 * than it assumes. The margin pays for that difference. It is what makes the
 * feature honest rather than merely present: at full pace it would brake at the
 * theoretical last moment and run a real player wide, which is exactly the
 * failure it exists to remove.
 */
const AUTO_PACE = 0.82;
/**
 * Longer than the autopilot's 90 m. That driver is braking for speeds it also
 * chose; this one has to cover a player arriving at a corner far faster than
 * any line through it allows, and the scan has to reach back far enough to
 * still be able to stop them.
 */
const AUTO_SCAN = 140; // m
/** Starts squeezing at a quarter of capacity and is flat out by four fifths —
 *  earlier and more gradual than the autopilot, so the pedal is already on
 *  before a player would have thought about it. */
const AUTO_FLOOR = 0.25;
const AUTO_SPAN = 0.55;

/**
 * Automatic braking: presses the brake for the player.
 *
 * Not to be confused with the `brakeAssist` of `realism.ts`, which only ever
 * *bleeds* pressure the player already applied (see `vehicle.ts`) and so cannot
 * slow the kart on its own. This adds pedal, so a player who never touches the
 * brake still makes every corner.
 *
 * It is an **input** aid, not a physics change. It raises the brake byte before
 * `Session.tick` ever sees it, so the recorded trace contains the assisted input
 * and replaying that trace reproduces the lap exactly — the ghost, the replay
 * bar and a server re-simulation all keep working untouched. Grip, brake force
 * and the solved ideal lap are not modified, which is what leaves the
 * realism-mode contract in CLAUDE.md intact.
 *
 * It does change lap times, so laps driven with it on are recorded under their
 * own key and never submitted to the leaderboard.
 */
export class AutoBrake {
  constructor(
    private readonly track: Track,
    private readonly profile: SpeedProfile,
    private readonly query: TrackQuery,
    private readonly weather: Weather,
  ) {}

  /** Brake demand 0..1 for this tick, to be taken alongside the player's own. */
  sample(kart: KartState): number {
    const here = this.query.sample(kart.x, kart.y);
    // Wet patches lower both the profile's speeds and the deceleration actually
    // available, so read grip where the kart is rather than assuming dry track.
    const brakeCap = maxBrakeDecel(this.weather.muAt[here.index]);
    return scanBrakeDemand(this.track, this.profile, here.index, speedOf(kart), {
      scan: AUTO_SCAN,
      pace: AUTO_PACE,
      brakeCap,
      floor: AUTO_FLOOR,
      span: AUTO_SPAN,
    });
  }
}
